import React, { useState, useEffect } from "react"
import { MapPin, X, AlertCircle, Loader2, Check } from "lucide-react"
import { CitySearchInput } from "@/components/dashboard/shared/CitySearchInput"
import { useAddCompanyAddress } from "@/hooks/seller/useAddCompanyAddress"
import { useAddBuyerAddress } from "@/hooks/buyer/useAddBuyerAddress"
import { addressSchema } from "@/lib/schemas/address.schema"
import { cn } from "@/lib/utils"

interface AddAddressFormProps {
  companyId?: string | null | undefined
  onSuccess: (newAddress?: any) => void
  onCancel: () => void
  isInline?: boolean
  type?: "buyer" | "seller"
}

export function AddAddressForm({
  companyId,
  onSuccess,
  onCancel,
  isInline = true,
  type = "seller"
}: AddAddressFormProps) {
  const [addressTitle, setAddressTitle] = useState("")
  const [addressLine1, setAddressLine1] = useState("")
  const [addressLine2, setAddressLine2] = useState("")
  const [addressCity, setAddressCity] = useState<any>(null)
  const [addressState, setAddressState] = useState("")
  const [addressPostalCode, setAddressPostalCode] = useState("")
  const [addressCountry, setAddressCountry] = useState("")
  const [addressIsDefault, setAddressIsDefault] = useState(false)
  const [addressError, setAddressError] = useState("")
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const addCompanyAddressMutation = useAddCompanyAddress(companyId)
  const addBuyerAddressMutation = useAddBuyerAddress()
  
  const isBuyer = type === "buyer"
  const addAddressMutation = isBuyer ? addBuyerAddressMutation : addCompanyAddressMutation

  const clearFieldError = (field: string) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  useEffect(() => {
    if (addressCity) {
      const stateVal = addressCity.stateName || addressCity.state || ""
      const countryVal = addressCity.countryName || addressCity.country || ""
      if (stateVal) {
        setAddressState(stateVal)
        clearFieldError("addressState")
      }
      if (countryVal) {
        setAddressCountry(countryVal)
        clearFieldError("addressCountry")
      }
    }
  }, [addressCity])

  const handleAddressSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const validationResult = addressSchema.safeParse({
      addressTitle,
      addressLine1,
      addressLine2: addressLine2 || "",
      addressCity,
      addressState,
      addressPostalCode,
      addressCountry,
      addressIsDefault,
    })

    if (!validationResult.success) {
      const formatted: Record<string, string> = {}
      validationResult.error.issues.forEach((issue) => {
        const field = issue.path[0] as string
        if (!formatted[field]) {
          formatted[field] = issue.message
        }
      })
      setFieldErrors(formatted)
      return
    }

    setFieldErrors({})
    setAddressError("")

    const validData = validationResult.data
    const cityId = addressCity.cityId || addressCity.object?.cityId || addressCity._id || ""

    addAddressMutation.mutate(
      {
        title: validData.addressTitle,
        addressLine1: validData.addressLine1,
        addressLine2: validData.addressLine2 || undefined,
        city: addressCity.city || addressCity.name || "",
        cityId,
        state: validData.addressState,
        postalCode: validData.addressPostalCode,
        country: validData.addressCountry,
        isDefault: addressIsDefault,
      },
      {
        onSuccess: (data) => {
          onSuccess(data)
          setAddressTitle("")
          setAddressLine1("")
          setAddressLine2("")
          setAddressCity(null)
          setAddressState("")
          setAddressPostalCode("")
          setAddressCountry("")
          setAddressIsDefault(false)
          setAddressError("")
          setFieldErrors({})
        },
        onError: (err: any) => {
          setAddressError(err?.message || "Failed to add address. Please try again.")
        }
      }
    )
  }

  return (
    <div className={`${isInline ? "rounded-xl bg-primary/[0.02] border border-primary/20 p-5 shadow-sm space-y-4" : ""}`}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-[11px] font-black text-slate-800 flex items-center gap-1.5 uppercase tracking-wide">
          <MapPin className="size-4 text-primary" /> Add New Address
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="text-slate-400 hover:text-slate-600 transition-colors p-0.5 rounded-md hover:bg-slate-100 cursor-pointer"
        >
          <X className="size-4" />
        </button>
      </div>

      {addressError && (
        <div className="p-3 bg-primary/5 text-primary text-xs font-semibold rounded-lg border border-primary/20 flex items-center gap-2">
          <AlertCircle className="size-4 shrink-0" />
          <span>{addressError}</span>
        </div>
      )}

      <div className="space-y-4 text-xs font-sans">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Address Title */}
          <div className="space-y-1">
            <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
              Address Title *
            </label>
            <input
              type="text"
              value={addressTitle}
              onChange={(e) => {
                setAddressTitle(e.target.value)
                clearFieldError("addressTitle")
              }}
              placeholder="e.g. Headquarters, Warehouse"
              className={cn(
                "w-full rounded-lg border bg-white px-3 py-2 text-xs text-slate-800 focus:outline-none transition-colors",
                fieldErrors.addressTitle
                  ? "border-primary focus:border-primary ring-1 ring-primary/20"
                  : "border-slate-200 focus:border-primary"
              )}
            />
            {fieldErrors.addressTitle && (
              <p className="text-[11px] text-primary font-semibold mt-0.5">{fieldErrors.addressTitle}</p>
            )}
          </div>

          {/* City */}
          <div className="space-y-1">
            <CitySearchInput
              value={addressCity}
              onChange={(val) => {
                setAddressCity(val)
                clearFieldError("addressCity")
              }}
              error={!!fieldErrors.addressCity}
              label="City *"
              placeholder="Type to search city..."
            />
            {fieldErrors.addressCity && (
              <p className="text-[11px] text-primary font-semibold mt-0.5">{fieldErrors.addressCity}</p>
            )}
          </div>

          {/* Address Line 1 */}
          <div className="space-y-1">
            <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
              Address Line 1 *
            </label>
            <input
              type="text"
              value={addressLine1}
              onChange={(e) => {
                setAddressLine1(e.target.value)
                clearFieldError("addressLine1")
              }}
              placeholder="Street Address, P.O. Box"
              className={cn(
                "w-full rounded-lg border bg-white px-3 py-2 text-xs text-slate-800 focus:outline-none transition-colors",
                fieldErrors.addressLine1
                  ? "border-primary focus:border-primary ring-1 ring-primary/20"
                  : "border-slate-200 focus:border-primary"
              )}
            />
            {fieldErrors.addressLine1 && (
              <p className="text-[11px] text-primary font-semibold mt-0.5">{fieldErrors.addressLine1}</p>
            )}
          </div>

          {/* Address Line 2 */}
          <div className="space-y-1">
            <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
              Address Line 2 (Optional)
            </label>
            <input
              type="text"
              value={addressLine2}
              onChange={(e) => {
                setAddressLine2(e.target.value)
                clearFieldError("addressLine2")
              }}
              placeholder="Apartment, Suite, Unit, Building"
              className={cn(
                "w-full rounded-lg border bg-white px-3 py-2 text-xs text-slate-800 focus:outline-none transition-colors",
                fieldErrors.addressLine2
                  ? "border-primary focus:border-primary ring-1 ring-primary/20"
                  : "border-slate-200 focus:border-primary"
              )}
            />
            {fieldErrors.addressLine2 && (
              <p className="text-[11px] text-primary font-semibold mt-0.5">{fieldErrors.addressLine2}</p>
            )}
          </div>

          {/* State / Region */}
          <div className="space-y-1">
            <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
              State / Region *
            </label>
            <input
              type="text"
              value={addressState}
              onChange={(e) => {
                setAddressState(e.target.value)
                clearFieldError("addressState")
              }}
              placeholder="e.g. California, Delhi"
              className={cn(
                "w-full rounded-lg border bg-white px-3 py-2 text-xs text-slate-800 focus:outline-none transition-colors",
                fieldErrors.addressState
                  ? "border-primary focus:border-primary ring-1 ring-primary/20"
                  : "border-slate-200 focus:border-primary"
              )}
            />
            {fieldErrors.addressState && (
              <p className="text-[11px] text-primary font-semibold mt-0.5">{fieldErrors.addressState}</p>
            )}
          </div>

          {/* Postal / Zip Code */}
          <div className="space-y-1">
            <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
              Postal / Zip Code *
            </label>
            <input
              type="text"
              value={addressPostalCode}
              onChange={(e) => {
                setAddressPostalCode(e.target.value)
                clearFieldError("addressPostalCode")
              }}
              placeholder="e.g. 90210, 110001"
              className={cn(
                "w-full rounded-lg border bg-white px-3 py-2 text-xs text-slate-800 focus:outline-none transition-colors",
                fieldErrors.addressPostalCode
                  ? "border-primary focus:border-primary ring-1 ring-primary/20"
                  : "border-slate-200 focus:border-primary"
              )}
            />
            {fieldErrors.addressPostalCode && (
              <p className="text-[11px] text-primary font-semibold mt-0.5">{fieldErrors.addressPostalCode}</p>
            )}
          </div>

          {/* Country */}
          <div className="space-y-1">
            <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
              Country *
            </label>
            <input
              type="text"
              value={addressCountry}
              onChange={(e) => {
                setAddressCountry(e.target.value)
                clearFieldError("addressCountry")
              }}
              placeholder="e.g. United States, India"
              className={cn(
                "w-full rounded-lg border bg-white px-3 py-2 text-xs text-slate-800 focus:outline-none transition-colors",
                fieldErrors.addressCountry
                  ? "border-primary focus:border-primary ring-1 ring-primary/20"
                  : "border-slate-200 focus:border-primary"
              )}
            />
            {fieldErrors.addressCountry && (
              <p className="text-[11px] text-primary font-semibold mt-0.5">{fieldErrors.addressCountry}</p>
            )}
          </div>

          {/* Set as Default Address */}
          <div className="flex items-center gap-2 pt-5 select-none">
            <input
              id="addressIsDefault"
              type="checkbox"
              checked={addressIsDefault}
              onChange={(e) => setAddressIsDefault(e.target.checked)}
              className="size-4 accent-primary cursor-pointer rounded"
            />
            <label htmlFor="addressIsDefault" className="text-xs font-bold text-slate-700 cursor-pointer">
              Set as Default Address
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="text-xs font-bold uppercase tracking-wider text-slate-600 border border-slate-200 hover:bg-slate-50 px-2 py-2 rounded-lg transition-all cursor-pointer h-9"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAddressSubmit}
            disabled={addAddressMutation.isPending}
            className="text-xs font-bold uppercase tracking-wider text-primary-foreground bg-primary hover:bg-primary-hover px-5 py-2 rounded-lg transition-all disabled:opacity-50 flex items-center gap-1.5 cursor-pointer h-9 shadow-md shadow-primary/20"
          >
            {addAddressMutation.isPending ? (
              <><Loader2 className="size-3.5 animate-spin" /> Adding...</>
            ) : (
              <>Save</>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
